import React from "react";
import { FaEye, FaShieldAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ProfilePhotoCell from "../common/ProfilePhotoCell";
import { formatDate } from "./utils/formatDate";

const cellClass = "px-2 py-2 sm:px-3 sm:py-2 text-xs sm:text-sm";

function formatDateTime(dateString) {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dash(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

function yesNo(val) {
  if (val === 1 || val === true || val === "1") return "Yes";
  if (val === 0 || val === false || val === "0") return "No";
  return "—";
}

function webLabel(val) {
  if (val === 1 || val === true || val === "1") return "Web";
  if (val === 0 || val === false || val === "0") return "App";
  return "—";
}

const HEADERS = [
  "SR. NO",
  "Photo",
  "User ID",
  "First Name",
  "Last Name",
  "Email",
  "Gender",
  "Contact",
  "Birth Date",
  "Age",
  "Grew Up",
  "Active",
  "Premium",
  "Created",
  "Actions",
];

const COL_COUNT = HEADERS.length;

export default function TopUsersTable({
  users,
  backendPage,
  backendLimit,
  formatDate: formatDateProp,
  onVerifyUser,
}) {
  const navigate = useNavigate();
  const format = formatDateProp || formatDate;

  if (!users.length) {
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow min-w-0">
        <table className="min-w-full text-left border-collapse min-w-[1400px]">
          <thead className="bg-gray-50">
            <tr>
              {HEADERS.map((label) => (
                <th key={label} className={cellClass}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                colSpan={COL_COUNT}
                className="px-4 py-8 text-center text-sm text-gray-500">
                No users found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow min-w-0">
      <table className="min-w-full text-left border-collapse min-w-[1400px]">
        <thead className="bg-gray-50 text-gray-700">
          <tr>
            {HEADERS.map((label) => (
              <th
                key={label}
                className={`${cellClass} font-semibold uppercase tracking-wider whitespace-nowrap`}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((user, idx) => (
            <tr
              key={user.id}
              className="hover:bg-gray-50 border-b border-gray-100 last:border-0">
              <td className={cellClass}>
                {(backendPage - 1) * backendLimit + idx + 1}
              </td>
              <td className={`${cellClass} w-[72px]`}>
                <ProfilePhotoCell
                  imageUrl={user.imageUrl || user.imageurl}
                  displayName={`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}
                />
              </td>
              <td className={`${cellClass} text-gray-700 tabular-nums`}>
                {user.id ?? "—"}
              </td>
              <td className={`${cellClass} font-medium text-gray-900 whitespace-nowrap`}>
                {dash(user.firstName)}
              </td>
              <td className={`${cellClass} whitespace-nowrap`}>{dash(user.lastName)}</td>
              <td className={`${cellClass} text-gray-600 max-w-[140px] sm:max-w-[200px] truncate`}>
                {dash(user.email)}
              </td>
              <td className={`${cellClass} capitalize whitespace-nowrap`}>
                {dash(user.gender)}
              </td>
              <td className={`${cellClass} whitespace-nowrap`}>{dash(user.contactNo)}</td>
              <td className={`${cellClass} whitespace-nowrap`}>
                {user.birthDate ? format(user.birthDate) : "—"}
              </td>
              <td className={`${cellClass} tabular-nums`}>
                {user.age != null && user.age !== "" ? user.age : "—"}
              </td>
              <td className={`${cellClass} max-w-[120px] truncate`}>{dash(user.grewUp)}</td>
              <td className={cellClass}>
                <span
                  className={
                    user.isActive === 1 || user.isActive === true
                      ? "text-green-600 font-medium"
                      : "text-gray-500"
                  }>
                  {yesNo(user.isActive)}
                </span>
              </td>
              <td className={cellClass}>
                <span
                  className={
                    user.premium === 1 || user.premium === true
                      ? "text-amber-700 font-medium"
                      : "text-gray-600"
                  }>
                  {yesNo(user.premium)}
                </span>
              </td>
              <td className={`${cellClass} whitespace-nowrap`}>
                {user.createdDate ? formatDateTime(user.createdDate) : "—"}
              </td>
              <td className={cellClass}>
                <div className="flex gap-2 sm:gap-3 items-center">
                  <button
                    type="button"
                    onClick={() => navigate(`/userdetails/${user.id}`)}
                    className="relative group p-1 rounded hover:bg-gray-100"
                    title="View">
                    <FaEye className="text-red-500 cursor-pointer" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                      View
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onVerifyUser(user)}
                    className="relative group p-1 rounded hover:bg-gray-100"
                    title="Verify profile pic">
                    <FaShieldAlt className="text-red-500 cursor-pointer" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 hidden group-hover:block bg-gray-700 text-white text-xs text-center px-2 py-1 rounded w-[135px] z-10">
                      Verify User Profile Pic
                    </span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
